import {
  checkUpdatePostalAddressRequired,
  getInsuranceQuoteById,
  postInsuranceQuoteCreateQuote,
  postInsuranceQuoteApplyRules,
  postInsuranceQuoteCalculatePrice,
  postInsuranceQuoteUpdateQuote,
  postInsuranceSubmitQuote,
  putUpdatePostalAddress,
  postInsuranceQuoteSendQuoteEmail,
  getInsurancePoliciesUrl,
  getSessionIdUrl,
} from '@/constants/forms-api.const';
import { RequestServices as _http } from '../common/request.services';
import { IQuote, ISubmitQuote } from '@/interfaces';
import { IQErrorReasonTypes } from '@/enums/iq-error-reason-types.enum';

export interface QuoteResponse {
  quote: IQuote;
  lastUpdated?: Date;
}

export interface IQErrorReason {
  typeOfReason: IQErrorReasonTypes;
  code: string;
}

export interface QuoteRulesResponse {
  quote: IQuote;
  reasonCodes: IQErrorReason[];
}

export interface SubmitQuoteResponse {
  quote: IQuote;
  success: boolean;
  result: number;
  errors: SubmitQuoteError[];
}

export interface SubmitQuoteError {
  errorType: number;
  errorDescription: string;
}

export const InsuranceQuoteService = {
  /**
   * Fetch quote details
   *
   * @param quoteId
   * @returns
   */
  fetchQuote(sessionId: string): Promise<QuoteResponse> {
    return _http.HttpGet(getInsuranceQuoteById.replace('{0}', sessionId));
  },

  /**
   * Generate session id
   *
   * @param quoteId
   * @returns
   */
  getSessionId(quoteId: string): Promise<string> {
    return _http.HttpGet(getSessionIdUrl.replace('{0}', quoteId));
  },

  /**
   * Create new quote
   *
   * @param quote
   */
  createQuote(quote: Partial<IQuote>): Promise<QuoteResponse> {
    return _http.HttpPost(postInsuranceQuoteCreateQuote, { quote });
  },

  /**
   * Apply business rules
   *
   * @param quote
   */
  applyBusinessRules(quoteId: string, quote: Partial<IQuote>): Promise<QuoteRulesResponse> {
    return _http
      .HttpPost(postInsuranceQuoteApplyRules.replace('{0}', quoteId), { quote })
      .then((res: QuoteRulesResponse) => {
        if (res.reasonCodes?.length) {
          res.reasonCodes = res.reasonCodes.filter((reason) => {
            return reason.typeOfReason != IQErrorReasonTypes.ValueAdjusted;
          });
        }
        return res;
      });
  },

  /**
   * Save quote
   *
   * @param quote
   */
  saveQuote(quoteId: string, quote: Partial<IQuote>): Promise<QuoteResponse> {
    return _http.HttpPost(postInsuranceQuoteUpdateQuote.replace('{0}', quoteId), { quote });
  },

  /**
   * Calculate price
   *
   * @param quote
   */
  calculatePrice(quoteId: string, quote: Partial<IQuote>): Promise<QuoteRulesResponse> {
    return _http.HttpPost(postInsuranceQuoteCalculatePrice.replace('{0}', quoteId), { quote });
  },

  /**
   * Check update postal address is required
   *
   * @param partyId
   * @param dpid
   */
  checkUpdatePostalAddressRequired(partyId: string, dpid: number | string) {
    return _http.HttpGet(checkUpdatePostalAddressRequired.replace('{0}', partyId).replace('{1}', dpid.toString()));
  },

  /**
   * Submit Quote
   *
   * @param quote
   */
  submitQuote(quoteId: string, submitQuote: ISubmitQuote): Promise<SubmitQuoteResponse> {
    return _http.HttpPost(postInsuranceSubmitQuote.replace('{0}', quoteId), submitQuote);
  },

  updatePostalAddress(d365Id: string, postalAddress: any) {
    return _http.HttpPut(putUpdatePostalAddress.replace('{0}', d365Id) + `&addressType=Postal`, postalAddress);
  },

  /**
   * Send Quote Email
   *
   * @param quoteId
   * @param destinationAddress
   */
  sendQuoteEmail(quoteId: string, destinationAddress: string) {
    return _http.HttpPost(postInsuranceQuoteSendQuoteEmail.replace('{0}', quoteId), {
      destinationAddress,
    });
  },

  /**
   * Get list of insurance policies
   * @returns
   */
  fetchMemberInsurancePolicies() {
    return _http.HttpGet(getInsurancePoliciesUrl);
  },
};
